import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { OrganizationService } from '~console/app/~shared/services/organization.service';
import * as OrganizationActions from '~console/app/~store/actions/organization.actions';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Organization,
  OrganizationUser
} from '~core/models/organization.models';
import { ORGANIZATION_STATE_TOKEN } from '~console/app/~shared/constants/global.constants';
import { HttpErrorResponse } from '@angular/common/http';
import { GlobalFacade } from '~console/app/~store/facades/global.facade';
import { OrganizationStateModel } from '~console/app/~shared/models/store.models';
import { Pagination, UserPreferences } from '~core/models/core.models';
import { CustomerSupplier } from '~core/models/customer-supplier.model';
import { AuthFacade } from '../facades/auth.facade';

export const DEFAULT_ORGANIZATION_STATE: OrganizationStateModel = {
  organizations: null,
  selectedOrganization: null,
  organizationUsers: null,
  partners: null,
  preferences: null
};

@State({
  name: ORGANIZATION_STATE_TOKEN,
  defaults: DEFAULT_ORGANIZATION_STATE
})
@Injectable()
export class OrganizationState {
  private _organizationService = inject(OrganizationService);
  private _globalFacade = inject(GlobalFacade);
  private _authFacade = inject(AuthFacade);

  @Action(OrganizationActions.LoadOrganizations)
  loadOrganizations(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.LoadOrganizations
  ): Observable<Pagination<Organization>> {
    return this._organizationService.list(action.params).pipe(
      tap({
        next: res => ctx.patchState({ organizations: res }),
        error: (err: HttpErrorResponse) =>
          this._globalFacade.handleFormViolations(err)
      })
    );
  }

  @Action(OrganizationActions.LoadOrganization)
  loadOrganization(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.LoadOrganization
  ): Observable<Organization> {
    return this._organizationService
      .read(action.organizationId, action.params)
      .pipe(
        tap({
          next: res => {
            ctx.patchState({ selectedOrganization: res });
            // refresh organization for global current user (logged user)
            this._authFacade.patchLoggedUser({
              organization: res
            });
          },
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.CreateOrganization)
  createOrganization(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.CreateOrganization
  ): Observable<Organization> {
    return this._organizationService.create(action.data).pipe(
      tap({
        next: res => {
          const state = ctx.getState();
          if (state.organizations) {
            ctx.patchState({
              organizations: {
                ...state.organizations,
                items: [...state.organizations.items, res]
              }
            });
          }
        },
        error: (err: HttpErrorResponse) =>
          this._globalFacade.handleFormViolations(err)
      })
    );
  }

  @Action(OrganizationActions.UpdateOrganization)
  updateOrganization(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.UpdateOrganization
  ): Observable<Organization> {
    return this._organizationService
      .update(action.organizationId, action.data)
      .pipe(
        tap({
          next: res => {
            const state = ctx.getState();
            if (state.organizations) {
              ctx.patchState({
                organizations: {
                  ...state.organizations,
                  items: state.organizations.items.map(i =>
                    i.id === action.organizationId ? res : i
                  )
                }
              });
            }
            if (state.selectedOrganization?.id === action.organizationId) {
              ctx.patchState({ selectedOrganization: res });
            }
          },
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.PatchOrganization)
  patchOrganization(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.PatchOrganization
  ): void {
    const state = ctx.getState();
    if (state.selectedOrganization?.id !== action.organizationId) {
      return;
    }
    ctx.patchState({
      selectedOrganization: { ...state.selectedOrganization, ...action.data }
    });
  }

  @Action(OrganizationActions.DeleteOrganization)
  deleteOrganization(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.DeleteOrganization
  ): Observable<unknown> {
    return this._organizationService.delete(action.organizationId).pipe(
      tap({
        next: () => {
          const state = ctx.getState();
          if (state.organizations) {
            ctx.patchState({
              organizations: {
                ...state.organizations,
                items: state.organizations.items.filter(
                  i => i.id !== action.organizationId
                )
              }
            });
          }
          if (state.selectedOrganization?.id === action.organizationId) {
            ctx.patchState({ selectedOrganization: null });
          }
        },
        error: (err: HttpErrorResponse) =>
          this._globalFacade.handleFormViolations(err)
      })
    );
  }

  @Action(OrganizationActions.LoadOrganizationUsers)
  loadOrganizationUsers(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.LoadOrganizationUsers
  ): Observable<Pagination<OrganizationUser>> {
    return this._organizationService
      .listUser(action.userId, action.params)
      .pipe(
        tap({
          next: res => ctx.patchState({ organizationUsers: res }),
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.UpdateOrganizationUser)
  updateOrganizationUser(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.UpdateOrganizationUser
  ): Observable<OrganizationUser> {
    return this._organizationService
      .updateUser(action.userId, action.data)
      .pipe(
        tap({
          next: res => {
            const state = ctx.getState();
            if (state.organizationUsers) {
              ctx.patchState({
                organizationUsers: {
                  ...state.organizationUsers,
                  items: state.organizationUsers.items.map(i =>
                    i.id === res.id ? res : i
                  )
                }
              });
            }
          },
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.UpdateOrganizationUserScopes)
  updateOrganizationUserScopes(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.UpdateOrganizationUserScopes
  ): Observable<OrganizationUser> {
    return this._organizationService
      .updateUserScopes(action.organizationId, action.userId, action.data)
      .pipe(
        tap({
          next: res => {
            const state = ctx.getState();
            if (state.organizationUsers) {
              ctx.patchState({
                organizationUsers: {
                  ...state.organizationUsers,
                  items: state.organizationUsers.items.map(i =>
                    i.id === res.id ? res : i
                  )
                }
              });
            }
          },
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.DeleteOrganizationUser)
  deleteOrganizationUser(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.DeleteOrganizationUser
  ): Observable<unknown> {
    return this._organizationService
      .deleteUser(action.userId, action.data)
      .pipe(
        tap({
          next: () => {
            ctx.dispatch(
              new OrganizationActions.LoadOrganizationUsers(action.userId)
            );
          },
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.UploadOrganizationLogo)
  uploadOrganizationLogo(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.UploadOrganizationLogo
  ): Observable<Organization> {
    return this._organizationService.uploadLogo(action.data).pipe(
      tap({
        next: res => {
          const state = ctx.getState();
          if (state.organizations) {
            ctx.patchState({
              organizations: {
                ...state.organizations,
                items: state.organizations.items.map(i =>
                  i.id === res.id ? res : i
                )
              }
            });
          }
          if (state.selectedOrganization?.id === res.id) {
            ctx.patchState({ selectedOrganization: res });
          }
        },
        error: (err: HttpErrorResponse) =>
          this._globalFacade.handleFormViolations(err)
      })
    );
  }

  @Action(OrganizationActions.LoadPartners)
  loadPartners(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.LoadPartners
  ): Observable<Pagination<CustomerSupplier>> {
    return this._organizationService
      .listPartners(action.organizationId, action.params)
      .pipe(
        tap({
          next: res => ctx.patchState({ partners: res }),
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.CreatePartner)
  createPartner(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.CreatePartner
  ): Observable<CustomerSupplier> {
    return this._organizationService
      .createPartner(action.organizationId, action.data)
      .pipe(
        tap({
          next: res => {
            const state = ctx.getState();
            if (state.partners) {
              ctx.patchState({
                partners: {
                  ...state.partners,
                  items: [...state.partners.items, res]
                }
              });
            }
          },
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.AddPartnerInOrganization)
  addPartnerInOrganization(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.AddPartnerInOrganization
  ): Observable<CustomerSupplier> {
    return this._organizationService
      .addPartnerInOrganization(action.organizationId, action.partnerId)
      .pipe(
        tap({
          next: res => {
            const state = ctx.getState();
            if (state.partners) {
              ctx.patchState({
                partners: {
                  ...state.partners,
                  items: [...state.partners.items, res]
                }
              });
            }
          },
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.PublicSearchOrganization)
  publicSearchOrganization(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.PublicSearchOrganization
  ): Observable<Organization> {
    return this._organizationService
      .publicSearch(action.organizationReference, action.ntd)
      .pipe(
        tap({
          next: res => ctx.patchState({ selectedOrganization: res }),
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.LoadOrganizationPreferences)
  loadOrganizationPreferences(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.LoadOrganizationPreferences
  ): Observable<UserPreferences> {
    return this._organizationService
      .readPreferences(action.organizationId)
      .pipe(
        tap({
          next: res => ctx.patchState({ preferences: res }),
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.SetOrganizationPreferences)
  setOrganizationPreferences(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.SetOrganizationPreferences
  ): Observable<UserPreferences> {
    return this._organizationService
      .setPreferences(action.organizationId, action.preferences)
      .pipe(
        tap({
          next: res => ctx.patchState({ preferences: res }),
          error: (err: HttpErrorResponse) =>
            this._globalFacade.handleFormViolations(err)
        })
      );
  }

  @Action(OrganizationActions.ClearSelectedOrganization)
  clearSelected(ctx: StateContext<OrganizationStateModel>): void {
    ctx.patchState({ selectedOrganization: null });
  }

  @Action(OrganizationActions.ResetOrganizationState)
  reset(ctx: StateContext<OrganizationStateModel>): void {
    ctx.setState(DEFAULT_ORGANIZATION_STATE);
  }

  @Selector()
  static organizations(
    state: OrganizationStateModel
  ): Pagination<Organization> | null {
    return state.organizations;
  }

  @Selector()
  static selectedOrganization(
    state: OrganizationStateModel
  ): Organization | null {
    return state.selectedOrganization;
  }

  @Selector()
  static organizationUsers(
    state: OrganizationStateModel
  ): Pagination<OrganizationUser> | null {
    return state.organizationUsers;
  }

  @Selector()
  static partners(
    state: OrganizationStateModel
  ): Pagination<CustomerSupplier> | null {
    return state.partners;
  }

  @Selector()
  static preferences(state: OrganizationStateModel): UserPreferences | null {
    return state.preferences;
  }
}
